import React from 'react';
import { SEAT_LABELS, handBeforeAvatar } from '../utils/tableSeats';
import { HAND_SLOT_COUNT } from '../utils/handLayout';
import { formatBidLabel } from '../utils/bidLabel';
import { useDoubleActivate } from '../utils/doubleActivate';
import SeatAvatar from './SeatAvatar';
import SeatHand from './SeatHand';
import PlayingCard from './PlayingCard';
import ScoreBagsDisplay from './ScoreBagsDisplay';

function trickCardSize(handMetrics) {
  const scale = handMetrics ? handMetrics.w / 42 : 1;
  return { w: Math.round(22 * scale), h: Math.round(30 * scale) };
}

/**
 * Seat trick pile.
 * - Spades (mode tricks): pile layers + trick count badge
 * - Hearts (mode points): same trick piles, badge shows hand points (♥=1, Q♠=13)
 */
export function TrickStack({
  visualSeat,
  count = 0,
  handMetrics,
  mode = 'tricks',
  points = 0,
}) {
  const { w: TRICK_CARD_W, h: TRICK_CARD_H } = trickCardSize(handMetrics);
  const isPoints = mode === 'points';
  const trickCount = count ?? 0;
  const pointValue = points ?? 0;
  // Always build the pile from tricks won (Hearts keeps the stack graphic)
  const layers = trickCount > 0 ? Math.min(trickCount, 3) : 1;
  const pileActive = trickCount > 0;
  const badgeValue = isPoints ? pointValue : trickCount;
  const showBadge = isPoints || trickCount > 0;
  const title = isPoints
    ? `${pointValue} pt${pointValue === 1 ? '' : 's'} · ${trickCount} trick${trickCount === 1 ? '' : 's'}`
    : (trickCount > 0 ? `${trickCount} tricks won` : undefined);

  return (
    <div
      className={[
        'spades-trick-stack-slot',
        `spades-trick-stack-slot--${visualSeat}`,
        isPoints ? 'spades-trick-stack-slot--points' : '',
        pileActive ? 'spades-trick-stack-slot--active' : 'spades-trick-stack-slot--empty',
      ].filter(Boolean).join(' ')}
      aria-hidden={!showBadge && !pileActive}
      title={title}
    >
      <div className={`spades-trick-stack spades-trick-stack--${visualSeat}`}>
        <div className="spades-trick-stack-cards">
          {Array.from({ length: layers }, (_, i) => (
            <PlayingCard
              key={i}
              faceDown
              width={TRICK_CARD_W}
              height={TRICK_CARD_H}
              className={[
                'spades-trick-stack-card',
                'spades-trick-stack-card--layer',
                !pileActive ? 'spades-trick-stack-card--placeholder' : '',
              ].filter(Boolean).join(' ')}
              style={{
                transform: `translate(${i * 2}px, ${i * 2}px)`,
                zIndex: i + 1,
              }}
            />
          ))}
        </div>
        {showBadge && (
          <span
            className={[
              'spades-trick-stack-count',
              isPoints ? 'spades-trick-stack-count--points' : '',
            ].filter(Boolean).join(' ')}
          >
            {badgeValue}
          </span>
        )}
      </div>
    </div>
  );
}

function resolveHandProps({
  isPlayerSeat,
  isPeekTarget,
  gamePlaying,
  empty,
  handCount,
  playerHand,
  peekHand,
}) {
  if (isPlayerSeat && playerHand) {
    const hasCards = !!(playerHand.cards?.length);
    const revealed = !!(playerHand.faceUp && hasCards);
    const count = gamePlaying ? (handCount || playerHand.count || 0) : 0;
    return {
      isYou: true,
      isPeek: false,
      faceUp: revealed,
      cards: gamePlaying && hasCards ? playerHand.cards : null,
      count,
      canRaise: gamePlaying && hasCards && !!playerHand.canRaise,
      canPlay: revealed && !!playerHand.canPlay,
      onRaiseCard: playerHand.onRaiseCard,
      onPlayCard: playerHand.onPlayCard,
    };
  }

  if (isPeekTarget && peekHand) {
    const hasCards = !!(peekHand.cards?.length);
    const revealed = !!(peekHand.faceUp && hasCards);
    const count = gamePlaying ? (peekHand.count || handCount || 0) : 0;
    return {
      isYou: false,
      isPeek: true,
      faceUp: revealed,
      cards: revealed && hasCards ? peekHand.cards : null,
      count,
      canRaise: false,
      canPlay: false,
      onRaiseCard: undefined,
      onPlayCard: undefined,
    };
  }

  if (gamePlaying && handCount > 0 && !isPlayerSeat && !isPeekTarget) {
    return {
      isYou: false,
      isPeek: false,
      faceUp: false,
      cards: null,
      count: handCount,
      canRaise: false,
      canPlay: false,
      onRaiseCard: undefined,
      onPlayCard: undefined,
    };
  }

  return {
    isYou: isPlayerSeat,
    isPeek: false,
    faceUp: false,
    cards: null,
    count: 0,
    canRaise: false,
    canPlay: false,
    onRaiseCard: undefined,
    onPlayCard: undefined,
  };
}

function SpadesSeat({
  absoluteSeat,
  visualClass,
  gridClass,
  occupant,
  isYou,
  canPick,
  gamePlaying,
  game,
  isWatching,
  hideWatcherHands,
  handCounts,
  trickStackCount,
  /** Hearts: show running hand penalty points on the stack instead of tricks */
  trickStackMode = 'tricks',
  trickStackPoints = 0,
  watcherPeekSeat,
  onSeatClick,
  onPeekHand,
  onWatcherBotClick,
  viewerSeat,
  handMetrics,
  playerHand = null,
  peekHand = null,
  /** { score, bags } chip next to avatar (mobile / narrow on-table layout) */
  seatScore = null,
}) {
  const empty = !occupant;
  const isBot = occupant?.type === 'site_bot' || occupant?.type === 'external_bot';
  const isPeekTarget = isWatching
    && watcherPeekSeat === absoluteSeat
    && !hideWatcherHands;
  const handCount = handCounts?.[absoluteSeat] ?? 0;
  const isViewerSeat = viewerSeat != null && absoluteSeat === viewerSeat;
  const isPlayerSeat = !empty && isViewerSeat && !isWatching;
  const isYourSeat = isPlayerSeat || isYou;
  const handFirst = handBeforeAvatar(visualClass);
  const displayName = empty
    ? SEAT_LABELS[absoluteSeat]
    : (occupant.username || occupant.botLabel || '?');
  const isAfk = !empty && !!occupant.afk;
  const isTurn = game?.turnSeat === absoluteSeat
    && (game?.phase === 'playing' || game?.phase === 'bidding' || game?.phase === 'bidding_choice');
  const tricksWon = trickStackCount != null
    ? trickStackCount
    : (game?.trickWonCounts?.[absoluteSeat] ?? 0);
  const handPoints = trickStackPoints != null
    ? trickStackPoints
    : (game?.handPoints?.[absoluteSeat] ?? 0);
  const handleWatcherPeek = useDoubleActivate(() => {
    if (isWatching && !empty && gamePlaying && !hideWatcherHands) {
      onPeekHand?.(absoluteSeat);
    }
  });

  const useBotMenu = isWatching && gamePlaying && isBot;
  const usePeekHandler = isWatching && gamePlaying && !empty && !hideWatcherHands && !isBot;
  const seatActivate = useBotMenu
    ? () => onWatcherBotClick?.(absoluteSeat, displayName)
    : usePeekHandler
      ? handleWatcherPeek
      : () => onSeatClick(absoluteSeat);

  const bid = game?.bids?.[absoluteSeat];
  const showBid = bid != null
    && game?.phase !== 'dealing'
    && game?.phase !== 'hand_complete';

  const avatarSize = handMetrics?.w <= 26 ? 28
    : handMetrics?.w <= 34 ? 36
      : handMetrics?.w >= 90 ? 58
        : handMetrics?.w >= 74 ? 54
          : 48;

  const trickStack = gamePlaying && (!empty || handCount > 0) ? (
    <TrickStack
      visualSeat={visualClass}
      count={tricksWon}
      points={handPoints}
      mode={trickStackMode === 'points' ? 'points' : 'tricks'}
      handMetrics={handMetrics}
    />
  ) : null;

  const handProps = resolveHandProps({
    isPlayerSeat,
    isPeekTarget,
    gamePlaying,
    empty,
    handCount,
    playerHand,
    peekHand,
  });

  const renderHandZone = () => (
    <SeatHand
      visualSeat={visualClass}
      isYou={handProps.isYou}
      isPeek={handProps.isPeek}
      faceUp={handProps.faceUp}
      cards={handProps.cards}
      count={handProps.count}
      reserveSlots={HAND_SLOT_COUNT}
      reserveVisible={handProps.count === 0}
      canRaise={handProps.canRaise}
      canPlay={handProps.canPlay}
      onRaiseCard={handProps.onRaiseCard}
      onPlayCard={handProps.onPlayCard}
      cardW={handMetrics?.w}
      cardH={handMetrics?.h}
      stackStep={handMetrics?.step}
    />
  );

  return (
    <div
      className={[
        'spades-seat',
        gridClass,
        isYourSeat ? 'spades-seat--yours' : '',
        isPeekTarget ? 'spades-seat--peek-target' : '',
        isTurn ? 'spades-seat--turn-slot' : '',
      ].filter(Boolean).join(' ')}
    >
      {handFirst && renderHandZone()}
      <div className={`spades-seat-core spades-seat-core--${visualClass}`}>
        <div className="spades-seat-avatar-block">
          {trickStack}
          <button
            type="button"
            className={[
              'spades-seat-btn',
              empty ? 'spades-seat-btn--empty' : '',
              isYourSeat ? 'spades-seat-btn--yours' : '',
              canPick ? 'spades-seat-btn--pickable' : '',
              useBotMenu ? 'spades-seat-btn--bot-menu' : '',
              isPeekTarget ? 'spades-seat-btn--peek' : '',
              isTurn ? 'spades-seat-btn--turn' : '',
            ].filter(Boolean).join(' ')}
            onClick={seatActivate}
            onDoubleClick={seatActivate}
          >
            <SeatAvatar
              occupant={occupant}
              visualSeat={visualClass}
              isYou={isYourSeat}
              size={avatarSize}
              showHost={!!occupant?.isHost}
              showDealer={gamePlaying && game?.dealerSeat === absoluteSeat}
            />
            <span className={['spades-seat-name', isAfk ? 'spades-seat-name--afk' : ''].filter(Boolean).join(' ')}>
              {displayName}
              {isAfk ? <span className="spades-seat-afk-tag" title="Away"> AFK</span> : null}
            </span>
          </button>
          {showBid && (
            <span className={`spades-seat-bid spades-seat-bid--${visualClass}`}>
              {formatBidLabel(bid)}
            </span>
          )}
          {seatScore && (
            <span
              className={`spades-seat-score spades-seat-score--${visualClass}`}
              title={seatScore.hideBags ? 'Score' : 'Score · bags'}
            >
              {seatScore.hideBags ? (
                <span className="spades-score-value">{seatScore.score ?? 0}</span>
              ) : (
                <ScoreBagsDisplay score={seatScore.score} bags={seatScore.bags} />
              )}
            </span>
          )}
        </div>
      </div>
      {!handFirst && renderHandZone()}
    </div>
  );
}

export default SpadesSeat;